import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Switch,
  Card,
  Typography,
  Divider,
  Space,
  Button,
  Alert,
  Tooltip,
} from 'antd';
import {
  InfoCircleOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { FeatureFlagStore, ShopStore } from 'stores';
import { checkExistingShopSettings } from 'utils/featureFlagUtils';

const { Title, Text, Paragraph } = Typography;

/**
 * Feature Flag Controls Component
 * Allows shop administrators to toggle experimental features
 */
const FeatureFlagControls = observer(() => {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settingsAnalysis, setSettingsAnalysis] = useState(null);

  useEffect(() => {
    // Feature flags are automatically loaded when shop changes via MobX reaction
    // Just analyze existing shop settings for the UI
    const analyzeSettings = async () => {
      try {
        const analysis = await checkExistingShopSettings();
        setSettingsAnalysis(analysis);
      } catch (error) {
        console.error('Failed to analyze shop settings:', error);
      }
    };

    analyzeSettings();
  }, []);

  const handleFlagChange = async (flagName, value) => {
    setLoading(true);
    try {
      await FeatureFlagStore.setShopFlag(flagName, value);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save feature flag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFlags = async () => {
    setLoading(true);
    try {
      await FeatureFlagStore.resetShopFlags();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to reset feature flags:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentFlags = () => {
    return FeatureFlagStore.getAllFlags();
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>
            <ExperimentOutlined /> Experimental Features
          </Title>
          <Paragraph type="secondary">
            Enable or disable experimental features for your shop. These
            features are under development and may change or be removed in
            future updates.
          </Paragraph>
        </div>

        {saved && (
          <Alert
            message="Feature flags saved successfully!"
            type="success"
            showIcon
            closable
          />
        )}

        <div>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* API Caching Feature */}
            <Card size="small" style={{ backgroundColor: '#fafafa' }}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Text strong>API Response Caching</Text>
                    <Tooltip title="Caches API responses to improve performance and reduce server load. Automatically invalidates when data changes.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 8, color: '#999' }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    checked={FeatureFlagStore.isApiCachingEnabled}
                    onChange={(value) => handleFlagChange('apiCaching', value)}
                    loading={loading}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Improves page load times by caching API responses for 5
                  minutes. Automatically clears when you make changes to data.
                </Text>
                {FeatureFlagStore.isApiCachingEnabled && (
                  <Text
                    type="secondary"
                    style={{ fontSize: '11px', fontStyle: 'italic' }}
                  >
                    ✅ Currently active - API responses are being cached
                  </Text>
                )}
              </Space>
            </Card>

            {/* Debug Mode Feature */}
            <Card size="small" style={{ backgroundColor: '#fafafa' }}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Text strong>Debug Mode</Text>
                    <Tooltip title="Shows detailed debug information in the browser console. Useful for troubleshooting issues.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 8, color: '#999' }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    checked={FeatureFlagStore.isDebugModeEnabled}
                    onChange={(value) => handleFlagChange('debugMode', value)}
                    loading={loading}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Enables detailed logging in the browser console. Useful for
                  troubleshooting performance issues.
                </Text>
                {FeatureFlagStore.isDebugModeEnabled && (
                  <Text
                    type="secondary"
                    style={{ fontSize: '11px', fontStyle: 'italic' }}
                  >
                    ⚠️ Debug mode active - Check browser console for detailed
                    logs
                  </Text>
                )}
              </Space>
            </Card>

            {/* Data Miner Menu Feature */}
            <Card size="small" style={{ backgroundColor: '#fafafa' }}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Text strong>Data Miner Menu</Text>
                    <Tooltip title="Shows the Data Miner menu item in the sidebar for accessing work order and asset data with sub-fleet support.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 8, color: '#999' }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    checked={
                      FeatureFlagStore.isEnabled('showDataMinerMenu') ||
                      FeatureFlagStore.isEnabled('showRawDataMenu')
                    }
                    onChange={(value) =>
                      handleFlagChange('showDataMinerMenu', value)
                    }
                    loading={loading}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Displays the Data Miner menu in the Fleet Analytics section.
                  Provides access to detailed work order and asset information
                  with sub-fleet aggregation.
                </Text>
                {(FeatureFlagStore.isEnabled('showDataMinerMenu') ||
                  FeatureFlagStore.isEnabled('showRawDataMenu')) && (
                  <Text
                    type="secondary"
                    style={{ fontSize: '11px', fontStyle: 'italic' }}
                  >
                    ⛏️ Data Miner menu is now visible in the sidebar
                  </Text>
                )}
              </Space>
            </Card>
          </Space>
        </div>

        <Divider />

        <div>
          <Space>
            <Button type="default" onClick={handleResetFlags} loading={loading}>
              Reset to Defaults
            </Button>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Shop: {ShopStore.currentShop?.name || 'Unknown'} (ID:{' '}
              {ShopStore.currentShop?.id || 'N/A'})
            </Text>
          </Space>
        </div>

        {/* Development Info (only show in dev mode) */}
        {import.meta.env.MODE === 'development' && (
          <Card size="small" style={{ backgroundColor: '#f0f0f0' }}>
            <Title level={5}>Development Info</Title>
            <pre style={{ fontSize: '10px', margin: 0 }}>
              {JSON.stringify(getCurrentFlags(), null, 2)}
            </pre>
          </Card>
        )}
      </Space>
    </Card>
  );
});

export default FeatureFlagControls;
